import { DD_CLIENT_KEY, ENV } from 'src/constants/env';
import { DATADOG_BASE_URL, DEFAULT_TAGS } from 'src/utils/datadog/constants';
import { createErrorFieldFromRawError } from 'src/utils/datadog/create-error-field-from-raw-error';
import {
  type BaseContext,
  type LogReason,
  type LogStatus,
  type RawError,
} from 'src/utils/datadog/types';
import { fetchWithRetry } from 'src/utils/fetch-with-retry';
import { getEntityId } from 'src/utils/get-entity-id';
import { v4 as uuidv4 } from 'uuid';

class DataDog {
  private shouldLogThisSession = false;

  constructor({ sessionSampleRate = 0 }) {
    if (sessionSampleRate <= 0) {
      this.shouldLogThisSession = false;
    }

    if (sessionSampleRate >= 100) {
      this.shouldLogThisSession = true;
    }

    this.shouldLogThisSession = Math.random() * 100 < sessionSampleRate;
  }

  private globalContext: BaseContext = {
    service: 'widget-integrations',
    source: 'browser',
    origin: 'logger',
    ddtags: DEFAULT_TAGS,
    http: {
      url: window.location.href,
      useragent: navigator.userAgent,
    },
    view: {
      url: window.location.href,
      referrer: document.referrer,
    },
  };

  private getLog({
    status,
    message,
    context,
    error,
    reason,
  }: {
    status: LogStatus;
    message: string;
    context?: Record<string, any>;
    error?: RawError;
    reason: LogReason;
  }): BaseContext {
    return {
      ...this.globalContext,
      message,
      date: new Date().getTime(),
      status,
      context: {
        ...context,
        reason,
      },
      error: createErrorFieldFromRawError(error),
    };
  }

  async init(type: 'widgets' | 'webview', experiment: Record<string, string>) {
    this.globalContext.type = type;
    this.globalContext.experiment = experiment;
    this.globalContext.session = { id: uuidv4() };
    this.globalContext.usr = {
      id: getEntityId(),
    };
  }

  async send(logs: BaseContext[]): Promise<void> {
    if (!this.shouldLogThisSession) {
      return;
    }
    if (!DD_CLIENT_KEY) {
      return;
    }

    try {
      await fetchWithRetry({
        url: DATADOG_BASE_URL.toString(),
        options: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(logs),
          keepalive: true,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error when sending the log:', error);
    }
  }

  async info(
    reason: LogReason,
    message: string,
    context?: Record<string, any>
  ) {
    const log = this.getLog({
      status: 'info',
      reason,
      message,
      context,
    });

    await this.send([log]);
  }

  async error(message: string, error: unknown) {
    const log = this.getLog({
      status: 'error',
      reason: 'error',
      message,
      error: error as RawError,
    });
    await this.send([log]);
  }

  async warn(
    reason: LogReason,
    message: string,
    context?: Record<string, any>
  ) {
    const log = this.getLog({
      status: 'warn',
      reason,
      message,
      context,
    });
    await this.send([log]);
  }
}

export const dataDog = new DataDog({
  sessionSampleRate: String(ENV) === 'prod' ? 0.1 : 100,
});
